import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { confirmEmployeeLogout } from "../auth/employeeLogout";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { EmptyState } from "../components/EmptyState";
import { Footer } from "../components/Footer";
import { Skeleton } from "../components/Spinner";
import { useToast } from "../components/Toast";
import { apiGet, apiSend } from "../lib/customFetch";
import styles from "./EnhederPage.module.css";

interface Session {
  id: string;
  type: "admin" | "medarbejder_enhed";
  label: string | null;
  lastSeenAt: string;
  rotatedAt: string;
  expiresAt: string;
  absoluteExpiresAt: string;
  current: boolean;
}

type LoadState =
  | { status: "loading" }
  | { status: "ok"; sessions: Session[] }
  | { status: "failed" };

const datoFormat = new Intl.DateTimeFormat("da-DK", {
  dateStyle: "medium",
  timeStyle: "short",
});

function formaterDato(value: string) {
  return datoFormat.format(new Date(value));
}

function sessionsnavn(session: Session) {
  if (session.label !== null && session.label.trim().length > 0) {
    return session.label;
  }
  return session.type === "admin" ? "Administrativ session" : "Medarbejder";
}

/** Brugerens aktive cookie-sessioner. Siden viser ingen tokenværdier. */
export function EnhederPage({ standalone = false }: { standalone?: boolean }) {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const toast = useToast();
  const [, navigate] = useLocation();

  const ramme = (content: ReactNode) => {
    if (!standalone) return content;
    return (
      <div className={styles.employeePage}>
        <header className={styles.employeeTop}>
          <div className={styles.brand}>
            app<span>smeden</span>
          </div>
          <nav className={styles.employeeNav} aria-label="Konto">
            <Link href="/stemple">Tilbage til tidsregistrering</Link>
            <button
              type="button"
              onClick={() => {
                if (!confirmEmployeeLogout()) return;
                void apiSend("POST", "/api/logout").finally(() =>
                  navigate("/login"),
                );
              }}
            >
              Log ud på denne enhed
            </button>
          </nav>
        </header>
        {content}
        <Footer />
      </div>
    );
  };

  const hent = useCallback(() => {
    setState({ status: "loading" });
    apiGet<Session[]>("/api/me/sessions")
      .then((sessions) => setState({ status: "ok", sessions }))
      .catch(() => setState({ status: "failed" }));
  }, []);

  useEffect(hent, [hent]);

  const tilbagekald = (session: Session) => {
    setRevokingId(session.id);
    apiSend("DELETE", `/api/me/sessions/${session.id}`)
      .then(() => {
        setState((current) =>
          current.status === "ok"
            ? {
                status: "ok",
                sessions: current.sessions.filter((item) => item.id !== session.id),
              }
            : current,
        );
        toast.show(`${sessionsnavn(session)} er logget ud.`, "success");
      })
      .catch(() =>
        toast.show("Enheden kunne ikke logges ud — prøv igen.", "error"),
      )
      .finally(() => setRevokingId(null));
  };

  if (state.status === "loading") {
    return ramme(
      <div className={styles.side} aria-label="Henter enheder">
        <Skeleton height="8rem" />
        <Skeleton height="8rem" />
      </div>,
    );
  }

  if (state.status === "failed") {
    return ramme(
      <Card>
        <EmptyState
          title="Kunne ikke hente enheder"
          description="Tjek din forbindelse og prøv igen."
          cta={<Button onClick={hent}>Prøv igen</Button>}
        />
      </Card>,
    );
  }

  return ramme(
    <div className={styles.side}>
      <header className={styles.intro}>
        <h1>Enheder</h1>
        <p>
          Se, hvor du er logget ind. Log ukendte eller gamle enheder ud med det
          samme.
        </p>
      </header>

      {state.sessions.length === 0 ? (
        <Card>
          <EmptyState
            title="Ingen aktive enheder"
            description="Der er ingen aktive enheder at vise."
          />
        </Card>
      ) : (
        <ul className={styles.liste} aria-label="Aktive enheder">
          {state.sessions.map((session) => (
            <li key={session.id}>
              <Card
                title={
                  <span className={styles.titel}>
                    {sessionsnavn(session)}
                    {session.current && (
                      <span className={styles.badge}>Denne enhed</span>
                    )}
                  </span>
                }
                actions={
                  session.current ? undefined : (
                    <Button
                      variant="destructive"
                      disabled={revokingId !== null}
                      onClick={() => tilbagekald(session)}
                    >
                      {revokingId === session.id ? "Logger ud…" : "Log enhed ud"}
                    </Button>
                  )
                }
              >
                <dl className={styles.metadata}>
                  <div>
                    <dt>Senest aktiv</dt>
                    <dd>{formaterDato(session.lastSeenAt)}</dd>
                  </div>
                  <div>
                    <dt>Logget ind</dt>
                    <dd>{formaterDato(session.rotatedAt)}</dd>
                  </div>
                  <div>
                    <dt>Udløber senest</dt>
                    <dd>{formaterDato(session.absoluteExpiresAt)}</dd>
                  </div>
                </dl>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>,
  );
}

export function MedarbejderEnhederPage() {
  return <EnhederPage standalone />;
}
